// Static pose debug mode (?debugPose=1&pose=1..5).
//
// Locomotion and pose bugs look identical on a moving character: you cannot tell "the foot mesh is
// mis-yawed" from "the gait clock is wrong" from "the IK is clamping" while all three are in
// motion. Freezing the rig in a known reference pose separates them -- and, more importantly,
// makes the answer MEASURABLE. In a T-pose every axis has a right answer: feet point along +Z,
// legs are vertical, arms are horizontal, and the two sides mirror. Those are dot products, not
// opinions, which means the machine can check them and nobody has to look at a picture.
//
// Poses are expressed as IK TARGETS wherever a limb is IK-driven, so the real solver runs. A pose
// that the solver cannot reach (a horizontal kick beyond leg length) reports as clamped rather
// than being quietly faked -- which is information, not a failure of the debugger.

const P2 = Math.PI / 2;

/** Put a foot target at a body-relative offset, in world space. */
function footAt(c, leg, dx, dy, dz) {
  const s = Math.sin(c.heading), co = Math.cos(c.heading);
  const hx = leg.spec.hip[0] + dx, hz = dz;
  leg.target.set(
    c.pos.x + hx * co + hz * s,
    c.pos.y + leg.spec.hip[1] + dy,
    c.pos.z - hx * s + hz * co);
  leg.contact = dy < -0.7;
  leg._roll = 0;
}

/**
 * Feet flat on the FLOOR under the hips -- the reference stance for every pose that is not a kick.
 * Anchored to the ground plane, not to an offset below the pelvis: with a body-relative offset a
 * one-legged pose has no defined height, and averaging the two targets floats the body upward (the
 * first run of the kick+punch pose left the SUPPORTING leg 0.44 m short of its own target).
 */
function stand(c) {
  for (const L of c.legs) {
    const s = Math.sin(c.heading), co = Math.cos(c.heading);
    const wx = c.pos.x + L.spec.hip[0] * co, wz = c.pos.z - L.spec.hip[0] * s;
    L.target.set(wx, c.world.groundAt(wx, wz, 0), wz);
    L.contact = true;
    L._roll = 0;
  }
}

function arms(c, fn) {
  for (let i = 0; i < c.arms.length; i++) {
    const sx = Math.sign(c.arms[i].position.x) || 1;   // -1 = anatomical RIGHT (body faces +Z)
    fn(c.arms[i], c.elbows[i], sx, i);
  }
}

export const POSES = {
  1: {
    name: 'T-pose',
    apply(c) {
      stand(c);
      // Straight out to the sides. An arm hangs -Y, and Rz(+pi/2) carries -Y to +X, so the sign
      // of the shoulder's own x is exactly the sign that sends each arm outward.
      arms(c, (sh, el, sx) => { sh.rotation.set(0, 0, sx * P2); el.rotation.set(0, 0, 0); });
    },
  },
  2: {
    name: 'A-pose',
    apply(c) {
      stand(c);
      arms(c, (sh, el, sx) => { sh.rotation.set(0, 0, sx * P2 * 0.5); el.rotation.set(0, 0, 0); });
    },
  },
  3: {
    name: 'Right kick front',
    apply(c) {
      stand(c);
      // Anatomical right = the leg whose hip sits at -X for a body facing +Z.
      const right = c.legs.find((L) => L.spec.hip[0] < 0) || c.legs[0];
      footAt(c, right, 0, 0.02, 0.80);
      arms(c, (sh, el, sx) => { sh.rotation.set(0.2, 0, sx * 0.55); el.rotation.set(-0.5, 0, 0); });
    },
  },
  4: {
    name: 'Left kick front',
    apply(c) {
      stand(c);
      const left = c.legs.find((L) => L.spec.hip[0] > 0) || c.legs[1];
      footAt(c, left, 0, 0.02, 0.80);
      arms(c, (sh, el, sx) => { sh.rotation.set(0.2, 0, sx * 0.55); el.rotation.set(-0.5, 0, 0); });
    },
  },
  5: {
    name: 'Right straight punch',
    apply(c) {
      stand(c);
      // Rx(-pi/2) carries the arm's -Y down-axis to +Z, i.e. straight along the body's forward.
      arms(c, (sh, el, sx) => {
        if (sx < 0) { sh.rotation.set(-P2, 0, 0); el.rotation.set(0, 0, 0); }        // right: extended
        else { sh.rotation.set(-1.15, 0, 0.32); el.rotation.set(-1.45, 0, 0); }      // left: guard
      });
    },
  },
  6: {
    name: 'Punch + right front kick',
    apply(c) {
      stand(c);
      // Right leg RAISED STRAIGHT FORWARD, horizontal at hip height. This is the pose the walking
      // hinge cap forbids (+22 deg thigh swing), so pose mode lifts the clamps -- see freeJoints.
      const right = c.legs.find((L) => L.spec.hip[0] < 0) || c.legs[0];
      const len = (right.spec.upper + right.spec.lower) * 0.985;
      footAt(c, right, 0, 0, len);              // level with the hip, straight out front
      arms(c, (sh, el, sx) => {
        if (sx < 0) { sh.rotation.set(-P2, 0, 0); el.rotation.set(0, 0, 0); }       // right: punch
        else { sh.rotation.set(-1.15, 0, 0.32); el.rotation.set(-1.45, 0, 0); }     // left: guard
      });
    },
  },
};

/**
 * Lift the WALKING hinge limits for inspection.
 *
 * The shipping biped caps thigh swing at +22 degrees and knee flexion at 40 (master brief), which
 * is correct for a walker and makes a horizontal front kick unreachable -- the first pose sweep
 * measured it clamping at z 0.32 with a 0.617 m residual. A debugger that silently clamps the pose
 * you asked to inspect is worse than useless, so pose mode widens the range on the SPEC (still
 * one-sided, so the knee still cannot invert) and says so in the report.
 */
export function freeJoints(c) {
  for (const L of c.legs) {
    if (L.spec._caps) continue;
    L.spec._caps = { leanLo: L.spec.leanLo, leanHi: L.spec.leanHi, kneeHi: L.spec.kneeHi, pitchHi: L.spec.pitchHi };
    L.spec.leanLo = -1.8;
    L.spec.leanHi = 2.6;
    L.spec.kneeHi = 2.4;
    L.spec.pitchHi = 2.9;
  }
}

/**
 * Freeze a rig into pose `id` and run the real IK on it.
 * Body/pelvis animation offsets are zeroed first: a pose is a POSE, not a pose plus whatever the
 * idle sway happened to be on that frame, or the numbers move under the gate.
 */
export function applyPose(c, id) {
  const P = POSES[id] || POSES[1];
  c.body.position.set(0, 0, 0);
  c.body.rotation.set(0, 0, 0);
  c.reachLean = 0;
  c.reachCrouch = 0;
  c.head.rotation.set(0, 0, 0);
  // Height FIRST, from the floor: the body stands on the ground at its own ride height, and every
  // target is then computed against a pelvis that is already where it belongs. Doing this after
  // the pose (from the mean of the targets) moves the body out from under the foot that was just
  // placed, which is a defect the pose gate measured as a 0.44 m miss on the supporting leg.
  c.pos.y = c.world.groundAt(c.pos.x, c.pos.z, 0) + c.ride;
  c.root.position.copy(c.pos);
  c.root.rotation.set(0, c.heading, 0);
  c.root.updateMatrixWorld(true);
  P.apply(c);
  c.root.position.copy(c.pos);
  c.root.updateMatrixWorld(true);
  c._inv.copy(c.body.matrixWorld).invert();
  for (const L of c.legs) L.solve(c._inv);
  c.root.updateMatrixWorld(true);
  return P.name;
}

/**
 * Axis report, in the rig's OWN root space. Every value here has a known right answer in a
 * reference pose, so a gate can assert on it instead of a person reading a screenshot.
 */
export function poseReport(c) {
  const THREE = c.root.constructor === Object ? null : null;   // (types come from the caller)
  const out = { legs: [], arms: [], heading: +c.heading.toFixed(4) };
  const inv = c.root.matrixWorld.clone().invert();
  const axis = (obj, col) => {
    const m = obj.matrixWorld.clone().premultiply(inv);
    const e = m.elements;
    const v = [e[col * 4], e[col * 4 + 1], e[col * 4 + 2]];
    const n = Math.hypot(...v) || 1;
    return v.map((x) => +(x / n).toFixed(4));
  };
  const pos = (obj) => {
    const m = obj.matrixWorld.clone().premultiply(inv);
    return [+m.elements[12].toFixed(4), +m.elements[13].toFixed(4), +m.elements[14].toFixed(4)];
  };
  for (const L of c.legs) {
    out.legs.push({
      hipX: L.spec.hip[0],
      side: L.spec.hip[0] < 0 ? 'right' : 'left',
      footFwd: axis(L.foot, 2),      // foot's +Z in body space -- should be world forward
      footUp: axis(L.foot, 1),
      footRight: axis(L.foot, 0),
      footPos: pos(L.foot),
      thighDown: axis(L.thigh, 1).map((v) => +(-v).toFixed(4)),
      // Did the solver actually reach the target, or clamp?
      reach: +Math.hypot(
        L.foot.matrixWorld.elements[12] - L.target.x,
        L.foot.matrixWorld.elements[13] - L.target.y,
        L.foot.matrixWorld.elements[14] - L.target.z).toFixed(4),
    });
  }
  for (let i = 0; i < c.arms.length; i++) {
    out.arms.push({
      side: c.arms[i].position.x < 0 ? 'right' : 'left',
      shoulderX: +c.arms[i].position.x.toFixed(3),
      armDown: axis(c.arms[i], 1).map((v) => +(-v).toFixed(4)),   // direction the upper arm points
      handPos: pos(c.elbows[i]),
    });
  }
  void THREE;
  return out;
}
